import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CreateScopeItemData, ScopeType } from '@services/scopeService';

const SCOPE_TYPES: ScopeType[] = ['page', 'component', 'api', 'document', 'view', 'widget'];

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateScopeItemData) => Promise<void>;
}

/**
 * REV-500: adding a scope item, as a dialog.
 *
 * It used to be an inline form wedged into the page header, which put three
 * controls and a button permanently above the list and still had nowhere to put
 * anything longer than a single line.
 *
 * A dialog buys the room for the second half of the ticket: a component-type
 * item can carry pasted markup, because a component often has no URL of its own
 * — you cannot point a scanner at "the date picker" — and the markup is what
 * makes the item testable at all.
 */
export default function AddScopeItemDialog({ open, onClose, onSubmit }: Props) {
  const { t } = useTranslation();

  const [identifier, setIdentifier] = useState('');
  const [scopeType, setScopeType] = useState<ScopeType>('page');
  const [title, setTitle] = useState('');
  const [markup, setMarkup] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isComponent = scopeType === 'component';

  /**
   * Move focus to the first field when the dialog opens.
   *
   * `autoFocus` alone does NOT do this here: MUI's focus trap puts focus on the
   * dialog container (a role="presentation" div) and the declarative attribute
   * silently loses. Verified in a browser, not assumed — focus was landing on
   * MuiDialog-container. Leaving it there is not a WCAG failure (focus is
   * inside the dialog) but it means a keyboard user has to tab before they can
   * type, and a screen reader announces a presentational wrapper first.
   */
  const identifierRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => identifierRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const reset = () => {
    setIdentifier('');
    setScopeType('page');
    setTitle('');
    setMarkup('');
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    // Validate on submit, never by pre-disabling Add: a disabled button cannot
    // say what is missing (platform form rule).
    if (!identifier.trim()) {
      setError(t('scope.validation.identifierRequired', 'Identifier is required'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const trimmedMarkup = markup.trim();
      await onSubmit({
        canonical_identifier: identifier.trim(),
        scope_type: scopeType,
        ...(title.trim() ? { title: title.trim() } : {}),
        // Only for a component, and only when non-empty. Sending '' would read
        // to the API as "clear the markup" on a re-add rather than "no
        // opinion"; sending it for a non-component would attach markup the user
        // can no longer see or correct.
        ...(isComponent && trimmedMarkup ? { markup: trimmedMarkup } : {}),
      });
      reset();
      onClose();
    } catch {
      // Stay open on failure — closing would discard everything the user typed.
      setError(t('scope.errors.addFailed', 'Failed to add scope item'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('scope.add.title', 'Add a scope item')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            inputRef={identifierRef}
            label={t('scope.add.identifier', 'URL or component identifier')}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            disabled={submitting}
            fullWidth
          />

          <FormControl fullWidth disabled={submitting}>
            <InputLabel id="scope-add-type-label">{t('scope.fields.type', 'Type')}</InputLabel>
            <Select
              labelId="scope-add-type-label"
              label={t('scope.fields.type', 'Type')}
              value={scopeType}
              onChange={(e) => setScopeType(e.target.value as ScopeType)}
            >
              {SCOPE_TYPES.map((tp) => (
                <MenuItem key={tp} value={tp}>
                  {tp}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t('scope.fields.titleOptional', 'Title (optional)')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={submitting}
            fullWidth
          />

          {isComponent && (
            <TextField
              label={t('scope.add.markup', 'Component markup (optional)')}
              value={markup}
              onChange={(e) => setMarkup(e.target.value)}
              disabled={submitting}
              multiline
              minRows={4}
              fullWidth
              inputProps={{ 'aria-describedby': 'scope-add-markup-help' }}
              helperText={
                <span id="scope-add-markup-help">
                  {t(
                    'scope.add.markupHelp',
                    'Paste the component’s HTML so it can be tested on its own. Stored encrypted.'
                  )}
                </span>
              }
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : t('scope.add.submit', 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
